import { roundNumber } from '@utils/miscUtils'

// Current provably-fair seeds -> render-ready view model.
export const toSeedsView = (data) => ({
  clientSeed: data?.clientSeed ?? '',
  hashedServerSeed: data?.hashedServerSeed ?? '',
  hashedNextServerSeed: data?.hashedNextServerSeed ?? '',
  nonce: data?.nonce ?? 0,
})

// Fairness recompute result -> render-ready view model.
export const toVerifyFairnessView = (data) => ({
  crashPoint: data?.crashMultiplier != null ? `${roundNumber(data.crashMultiplier)}x` : '—',
  crashMultiplier: data?.crashMultiplier ?? null,
})

// Finished-round fairness info -> render-ready view model.
// `raw` carries the untouched payload through to the local verifier, so the UI
// verifies the server's own numbers rather than reformatted copies of them.
//
// The engine stores the UNROUNDED multiplier (e.g. 2.1499446523023464). Display
// values are rounded to the game's 2dp precision so a round reads the same here
// as on its history chip; `crashMultiplier` keeps the exact value for verifying.
export const toRoundInfoView = (data, { formatDate }) => {
  if (!data) return null

  return {
    roundId: data.roundId,
    // Position of the round within its seed epoch — NOT a round identifier. It
    // restarts at 1 on every rotation, so it is always rendered alongside the
    // quota ("3 / 5") to make the scoping obvious.
    round: data.round,
    seedQuota: data.seedQuota ?? null,
    // The denominator is only informative when a seed serves more than one round.
    // At a quota of 1 (one seed per round) "1 / 1" is noise, and at 0 (auto-rotation
    // off) there is no denominator to show.
    seedRound:
      data.round == null
        ? null
        : data.seedQuota > 1
          ? `${data.round} / ${data.seedQuota}`
          : `${data.round}`,
    crashPoint: data.crashMultiplier != null ? `${roundNumber(data.crashMultiplier)}x` : '—',
    crashMultiplier: data.crashMultiplier ?? null,
    // Set when a restart forced an early settlement — the recorded multiplier is
    // then the last one seen, not what the seed derives.
    interrupted: data.interrupted === true,
    derivedCrashPoint:
      data.derivedCrashMultiplier != null ? `${roundNumber(data.derivedCrashMultiplier)}x` : '—',
    seed: data.seed,
    hash: data.hash,
    hex: data.hex,
    decimal: data.decimal,
    date: formatDate(data.createdAt),

    // Seed commitment chain
    epochId: data.epochId ?? null,
    serverSeedHash: data.serverSeedHash ?? null,
    serverSeed: data.serverSeed ?? null,
    seedStatus: data.seedStatus ?? null,
    seedRevealedAt: data.seedRevealedAt ? formatDate(data.seedRevealedAt) : null,
    isSeedRevealed: Boolean(data.serverSeed),

    // ── Provably fair version + the seeds it consumed ──
    // Defaulted to 1 for history written before the field existed. The UI branches
    // on THIS, never on whether clientSeeds is populated.
    provablyFairVersion: Number(data.provablyFairVersion ?? 1),
    // Kept in slot order — the order the backend accepted them, which is the order
    // they were hashed in. Re-sorting would change the derivation.
    clientSeeds: data.clientSeeds?.length
      ? [...data.clientSeeds]
          .sort((a, b) => Number(a.slot) - Number(b.slot))
          .map((entry) => ({
            slot: Number(entry.slot),
            clientSeed: entry.clientSeed,
            source: entry.source,
            isFallback: entry.source === 'SERVER_FALLBACK',
            playerId: entry.playerId ?? null,
            acceptedAt: entry.acceptedAt ?? null,
          }))
      : null,

    raw: data,
  }
}

// Algorithm parameters -> render-ready view model.
//
// Carries NO commitment: seeds are per round, so there is no system-wide current
// seed. The live round's commitment comes from the round-state events in the store.
export const toProvablyFairView = (data) => {
  if (!data) return null

  return {
    seedModel: data.seedModel ?? 'PER_ROUND',
    gameSeed: data.gameSeed ?? null,
    houseEdge: data.houseEdge ?? null,
    houseEdgePercent: data.houseEdge != null ? `${(data.houseEdge * 100).toFixed(0)}%` : '—',
    maxRawOutcome: data.maxRawOutcome ?? null,
    maxMultiplierCap: data.maxMultiplierCap ?? 0,
    separator: data.separator ?? ':',
    algorithm: data.algorithm ?? null,
    // The version NEW rounds are produced with. A specific round's version comes
    // from roundInfo — history is mixed and this must not be used to verify one.
    provablyFairVersion: Number(data.provablyFairVersion ?? 1),
    clientSeedSlots: Number(data.clientSeedSlots ?? 0),
  }
}
